import { yupResolver } from '@hookform/resolvers/yup'
import type { LocationRecord } from '@libs/data-access/entities'
import type { ShiftRecord } from '@libs/data-access/queries'
import { type ApiErrorResponseData } from '@libs/data-access/queries'
import { notifications } from '@libs/ui/ds'
import { useCommonNotificationsTranslation } from '@libs/utils/translations'
import { useMemoSelector } from '@spa/redux/hooks'
import { deleteEntityByIds } from '@spa/scenes/Plannings/redux/reducers'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { getCurrentAccount } from 'snap-redux/modules/accounts/selectors'
import {
  createBulkShifts,
  createShift,
  deleteShift,
  updateShift
} from 'snap-redux/modules/shifts'
import { getAllUniqueWeeklyschedules } from 'snap-redux/modules/weeklyschedules/selectors'
import { useActions } from 'snap-redux/utils'

import type FormData from '../Models/FormData'
import { useShiftSchemaValidations } from '../ShiftFormLegacy.validations'
import createShiftArrayPayloadLegacy from '../utils/createShiftArrayPayloadLegacy'

import useShiftDefaultValuesLegacy from './useShiftDefaultValuesLegacy'

type ShiftForm = {
  shift: ShiftRecord
  teamId: number
  labelId?: number
  weeklyScheduleIsPublished: boolean
  selectedTeamLocation?: LocationRecord
  weeklyscheduleLocation: LocationRecord
  weeklyScheduleWeek: string
  onFormSuccess: () => void
}

const actionCreators = {
  createShift,
  createBulkShifts,
  updateShift,
  deleteShift
}

/**
 * @deprecated please don't use it
 */
const useShiftFormLegacy = ({
  shift,
  teamId,
  labelId,
  weeklyScheduleIsPublished,
  selectedTeamLocation,
  weeklyscheduleLocation,
  weeklyScheduleWeek,
  onFormSuccess
}: ShiftForm) => {
  const dispatch = useDispatch()
  const [formLoading, setFormLoading] = useState(false)
  const [formError, setFormError] = useState<ApiErrorResponseData | null>(null)

  // TODO : To switch to RQ
  const account = useMemoSelector(getCurrentAccount)

  // TODO : To switch to RQ
  const weeklyschedules = useMemoSelector(getAllUniqueWeeklyschedules)

  const { t: tNotifications } = useCommonNotificationsTranslation()
  const shiftSchemaValidations = useShiftSchemaValidations()
  const actions = useActions(actionCreators)
  const isCreation = !shift?.id

  const defaultValues = useShiftDefaultValuesLegacy({
    shift,
    weeklyscheduleLocation,
    teamId,
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    labelId,
    weeklyScheduleIsPublished,
    selectedTeamLocation
  })

  const useFormMethods = useForm({
    mode: isCreation ? 'onSubmit' : 'onBlur',
    defaultValues,
    resolver: yupResolver(shiftSchemaValidations)
  })

  const filteredWeeklyschedules = weeklyschedules.filter(
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    wks => wks.week === weeklyScheduleWeek
  )

  const onSubmit = (formData: FormData) => {
    const newWeeklyschedule = filteredWeeklyschedules.find(
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      weeklyschedule => weeklyschedule.get('team_id') === teamId
    )

    const payload = createShiftArrayPayloadLegacy({
      formData,
      shift,
      weeklyscheduleLocation,
      account,
      newWeeklyschedule
    })

    setFormLoading(true)
    if (!isCreation) {
      return submitShiftUpdate(payload)
    }
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    const isBulk = !(payload.length === 1 && payload[0].length === 1)
    if (isBulk) {
      submitBulkShiftCreation(payload)
    } else {
      submitShiftCreation(payload)
    }
  }

  const submitShiftUpdate = payload => {
    actions
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      .updateShift(shift?.id, payload[0][0])
      .then(onFormSuccess)
      .catch((error: ApiErrorResponseData) => {
        setFormLoading(false)
        setFormError(error)
      })
  }

  const submitShiftCreation = payload => {
    actions
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      .createShift(payload[0][0])
      .then(onFormSuccess)
      .catch((error: ApiErrorResponseData) => {
        setFormLoading(false)
        setFormError(error)
      })
  }

  const submitBulkShiftCreation = payload => {
    const shiftPromises = payload.map(shiftBulk =>
      actions.createBulkShifts({ shifts: shiftBulk })
    )

    const promise = Promise.all(shiftPromises)

    notifications.promise(promise, {
      loading: { message: tNotifications('shift.bulk.loading') },
      success: { message: tNotifications('shift.bulk.success') },
      error: { message: tNotifications('shift.bulk.error') }
    })

    promise.then(onFormSuccess).catch(() => {
      setFormLoading(false)
    })
  }

  const onDeleteSubmit = ({ notify_user }) => {
    actions
      .deleteShift(shift?.id, { notify_user })
      .then(() => {
        // Trick to remove unassigned shifts from redux when you delete it. On update/create, no need, but on deletion, you need that
        if (shift?.id) {
          const payload = {
            entity: 'shifts' as const,
            ids: [shift.id]
          }
          dispatch(deleteEntityByIds(payload))
        }
      })
      .then(onFormSuccess)
      .catch(error => {
        setFormLoading(false)
        setFormError(error)
      })

    setFormLoading(true)
  }

  return {
    onDeleteSubmit,
    onSubmit,
    formLoading,
    formError,
    ...useFormMethods
  }
}

export default useShiftFormLegacy
